import {
    Box,
    Stack,
    Typography,
    Chip,
    Collapse,
} from '@compass/core';
import { Alert, Chip as MuiChip } from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import BarChartIcon from '@mui/icons-material/BarChart';
import TableChartIcon from '@mui/icons-material/TableChart';
import ChecklistIcon from '@mui/icons-material/Checklist';
import PieChartIcon from '@mui/icons-material/PieChart';
import DonutLargeIcon from '@mui/icons-material/DonutLarge';
import TableRowsIcon from '@mui/icons-material/TableRows';
import SchoolIcon from '@mui/icons-material/School';
import HowToRegIcon from '@mui/icons-material/HowToReg';
import RequestQuoteIcon from '@mui/icons-material/RequestQuote';
import LogoutSharpIcon from '@mui/icons-material/LogoutSharp';
import PeopleAltIcon from '@mui/icons-material/PeopleAlt';
import WcIcon from '@mui/icons-material/Wc';
import EventNoteSharpIcon from '@mui/icons-material/EventNoteSharp';
import AttachMoneySharpIcon from '@mui/icons-material/AttachMoneySharp';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import NumbersIcon from '@mui/icons-material/Numbers';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import CalendarMonthSharpIcon from '@mui/icons-material/CalendarMonthSharp';
import LocalAtmSharpIcon from '@mui/icons-material/LocalAtmSharp';
import LocalLibrarySharpIcon from '@mui/icons-material/LocalLibrarySharp';
import SchoolSharpIcon from '@mui/icons-material/SchoolSharp';
import SettingsSharpIcon from '@mui/icons-material/SettingsSharp';
import PhonelinkRingSharpIcon from '@mui/icons-material/PhonelinkRingSharp';
import SpellcheckIcon from '@mui/icons-material/Spellcheck';
import { Fragment, useState, useMemo } from 'react';
import { DashboardCardConfig, CardId } from '../types';
import { DashboardTabConfig, TabId, MAX_CARDS_PER_TAB } from '../types/DashboardTabConfig';
import { CATEGORY_CONFIGS } from '../config/categoryConfigs';
import { CARD_PAIR_GROUPS, CardPairGroup } from '../config/cardConfigs';
import { ClientKpiConfiguration, KpiDefinition, KpiViewType } from '../KpiDashboard/types';

interface DrawerCardsViewProps {
    tabs: DashboardTabConfig[];
    activeTabId: TabId;
    cards: DashboardCardConfig[];
    isAtCardLimit: boolean;
    onToggleCard: (cardId: CardId) => void;
    kpiDefinitions: KpiDefinition[];
    kpiConfiguration: ClientKpiConfiguration[];
    onToggleKpi: (kpiId: string, viewType: KpiViewType) => void;
}

interface CardDisplayGroup {
    group: CardPairGroup;
    variants: { card: DashboardCardConfig; chipLabel: string }[];
}

const NEW_ITEM_WINDOW_MS = 15 * 24 * 60 * 60 * 1000;

const isNewKpiDefinition = (definition: KpiDefinition): boolean => {
    if (!definition.createdDate) return false;
    const age = Date.now() - new Date(definition.createdDate).getTime();
    return age >= 0 && age <= NEW_ITEM_WINDOW_MS;
};


const CARD_ICON_MAP: Record<string, React.ElementType> = {
    'BarChart': BarChartIcon,
    'TableChart': TableChartIcon,
    'PieChart': PieChartIcon,
    'Checklist': ChecklistIcon,
    'School': SchoolIcon,
    'DonutLarge': DonutLargeIcon,
    'TableRows': TableRowsIcon,
    'HowToReg': HowToRegIcon,
    'RequestQuote': RequestQuoteIcon,
    'ExitToApp': LogoutSharpIcon,
    'PeopleAlt': PeopleAltIcon,
    'Wc': WcIcon,
    'SettingsSuggest': EventNoteSharpIcon,
    'Handshake': AttachMoneySharpIcon,
    'NotificationsActive': NotificationsActiveIcon,
};

const CATEGORY_ICON_MAP: Record<string, React.ElementType> = {
    'CalendarMonthSharp': CalendarMonthSharpIcon,
    'LocalAtmSharp': LocalAtmSharpIcon,
    'LocalLibrarySharp': LocalLibrarySharpIcon,
    'SchoolSharp': SchoolSharpIcon,
    'Spellcheck': SpellcheckIcon,
    'PhonelinkRingSharp': PhonelinkRingSharpIcon,
    'SettingsSharp': SettingsSharpIcon,
};


const buildDisplayGroups = (categoryCards: DashboardCardConfig[]): CardDisplayGroup[] => {
    const groups: CardDisplayGroup[] = [];

    for (const group of CARD_PAIR_GROUPS) {
        const matched: { card: DashboardCardConfig; chipLabel: string }[] = [];
        for (const variant of group.variants) {
            const card = categoryCards.find((c) => c.id === variant.cardId);
            if (card) matched.push({ card, chipLabel: variant.chipLabel });
        }
        if (matched.length >= 1) {
            groups.push({ group, variants: matched });
        }
    }

    return groups.sort((a, b) =>
        a.group.groupName.localeCompare(b.group.groupName, undefined, { sensitivity: 'base' })
    );
};


export const DrawerCardsView = ({
    tabs,
    activeTabId,
    cards,
    isAtCardLimit,
    onToggleCard,
    kpiDefinitions,
    kpiConfiguration,
    onToggleKpi,
}: DrawerCardsViewProps) => {
    const [expandedCategories, setExpandedCategories] = useState<Set<string>>(new Set());

    const activeTab = tabs.find((t) => t.id === activeTabId);
    const activeTabLabel = activeTab?.label ?? 'this tab';

    // Count from the tab's full card array so locked cards are included
    const enabledCardCount = useMemo(() => {
        return activeTab?.cards.filter((c) => c.enabled).length ?? 0;
    }, [activeTab]);

    const hasEnabledKpiSection = useMemo(
        () => kpiConfiguration.some((item) => item.enabled),
        [kpiConfiguration]
    );
    const totalEnabledCount = enabledCardCount + (hasEnabledKpiSection ? 1 : 0);
    const hasReachedCardLimit = isAtCardLimit || totalEnabledCount >= MAX_CARDS_PER_TAB;
    const availableKpiCardCount = kpiDefinitions.reduce(
        (count, definition) => count + (definition.supportsParent === false ? 1 : 2),
        0
    );

    const cardsByCategory = useMemo(() => {
        const grouped = new Map<string, DashboardCardConfig[]>();
        cards.forEach((card) => {
            const cat = card.category ?? 'Other';
            if (!grouped.has(cat)) {
                grouped.set(cat, []);
            }
            grouped.get(cat)!.push(card);
        });
        return grouped;
    }, [cards]);

    const toggleCategory = (categoryId: string) => {
        setExpandedCategories((prev) => {
            const next = new Set(prev);
            if (next.has(categoryId)) {
                next.delete(categoryId);
            } else {
                next.add(categoryId);
            }
            return next;
        });
    };

    const getCardIconComponent = (iconName: string) => {
        const IconComponent = CARD_ICON_MAP[iconName] || BarChartIcon;
        return <IconComponent />;
    };

    const getCategoryIconComponent = (iconName: string) => {
        const IconComponent = CATEGORY_ICON_MAP[iconName];
        return IconComponent ? <IconComponent /> : null;
    };

    const kpiMetricOptions = [...kpiDefinitions].sort((a, b) => a.defaultOrder - b.defaultOrder).map((definition) => (
        <Box key={definition.id} sx={{ p: 2, borderRadius: 1, border: 1, borderColor: 'divider', display: 'flex', alignItems: 'center', gap: 2, '&:hover': { bgcolor: 'action.hover' } }}>
            <Box sx={{ width: 40, height: 40, borderRadius: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: 'primary.main', color: 'primary.contrastText', flexShrink: 0 }}>
                <NumbersIcon />
            </Box>
            <Stack direction="row" spacing="xs" alignItems="center" sx={{ flexGrow: 1, minWidth: 0 }}>
                <Typography fontWeight="medium" sx={{ fontSize: '0.82rem', lineHeight: 1.2, overflowWrap: 'anywhere' }}>
                    {definition.label}
                </Typography>
                {isNewKpiDefinition(definition) && (
                    <Chip label="New" size="small" color="secondary" sx={{ height: 20, fontSize: '0.75rem' }} />
                )}
            </Stack>
            <Stack direction="row" spacing="xs" sx={{ flexShrink: 0 }}>
                {(['student', 'parent'] as KpiViewType[])
                    .filter((viewType) => definition.supportsParent !== false || viewType !== 'parent')
                    .map((viewType) => {
                    const enabled = kpiConfiguration.some((item) => item.kpiId === definition.id && item.viewType === viewType && item.enabled);
                    const isDisabled = hasReachedCardLimit && !hasEnabledKpiSection && !enabled;
                    return (
                        <MuiChip
                            key={viewType}
                            label={viewType === 'student' ? 'Student' : 'Parent'}
                            icon={enabled ? <CheckIcon sx={{ fontSize: 14 }} /> : undefined}
                            size="small"
                            variant={enabled ? 'filled' : 'outlined'}
                            onClick={() => onToggleKpi(definition.id, viewType)}
                            disabled={isDisabled}
                            sx={enabled ? {
                                bgcolor: (theme) => theme.palette.primary.light,
                                color: (theme) => theme.palette.primary.contrastText,
                                '& .MuiChip-icon': { color: (theme) => theme.palette.primary.contrastText },
                            } : { borderColor: (theme) => theme.palette.border.dark, color: 'primary.main' }}
                        />
                    );
                })}
            </Stack>
        </Box>
    ));

    return (
        <Box sx={{ flexGrow: 1, overflow: 'auto', p: 2 }}>
            <Box sx={{ mb: 2 }}>
                <Typography variant="body2" color="text.secondary">
                    Showing cards for <strong>{activeTabLabel}</strong>
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                    {totalEnabledCount} / {MAX_CARDS_PER_TAB} cards active
                </Typography>
            </Box>

            {hasReachedCardLimit && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                    Card limit reached for this tab. Remove a card before adding another.
                </Alert>
            )}

            <Stack direction="column" spacing="sm">
                {CATEGORY_CONFIGS.map((category) => {
                    const categoryCards = cardsByCategory.get(category.id) || [];
                    const isExpanded = expandedCategories.has(category.id);
                    const categoryCardCount = categoryCards.length + (category.id === 'Services' ? availableKpiCardCount : 0);

                    if (categoryCards.length === 0) return null;

                    const displayGroups = buildDisplayGroups(categoryCards);

                    return (
                        <Box key={category.id}>
                            {/* Category Header */}
                            <Box
                                onClick={() => toggleCategory(category.id)}
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 1.5,
                                    p: 1.5,
                                    cursor: 'pointer',
                                    borderRadius: 1,
                                    '&:hover': { bgcolor: 'action.hover' },
                                }}
                            >
                                <Box
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        color: 'primary.main',
                                    }}
                                >
                                    {getCategoryIconComponent(category.icon)}
                                </Box>
                                <Typography variant="subtitle1" fontWeight="bold" sx={{ flexGrow: 1 }}>
                                    {category.label}
                                </Typography>
                                <Chip label={categoryCardCount} size="small" sx={{ minWidth: 28 }} />
                                {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                            </Box>

                            {/* Category Cards */}
                            <Collapse in={isExpanded}>
                                <Stack direction="column" spacing="sm" sx={{ mt: 1, ml: 1 }}>
                                    {displayGroups.map(({ group, variants }) => {
                                        const hasActiveVariant = variants.some((v) => v.card.enabled);
                                        const hasNewVariant = variants.some((v) => v.card.isNew);

                                        return (
                                            <Fragment key={group.groupName}>
                                            <Box
                                                sx={{
                                                    p: 2,
                                                    borderRadius: 1,
                                                    border: 1,
                                                    borderColor: 'divider',
                                                    display: 'flex',
                                                    alignItems: 'flex-start',
                                                    gap: 2,
                                                    '&:hover': { bgcolor: 'action.hover' },
                                                }}
                                            >
                                                {/* Icon */}
                                                <Box
                                                    sx={{
                                                        width: 40,
                                                        height: 40,
                                                        borderRadius: 1,
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        bgcolor: hasActiveVariant ? 'primary.main' : 'action.selected',
                                                        color: hasActiveVariant ? 'primary.contrastText' : 'text.secondary',
                                                        flexShrink: 0,
                                                    }}
                                                >
                                                    {getCardIconComponent(group.icon)}
                                                </Box>

                                                {/* Content */}
                                                <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                                                    <Stack direction="row" spacing="sm" alignItems="center" sx={{ mb: 0.5 }}>
                                                        <Typography variant="subtitle1" fontWeight="medium">
                                                            {group.groupName}
                                                        </Typography>
                                                        {hasNewVariant && (
                                                            <Chip
                                                                label="New"
                                                                size="small"
                                                                color="secondary"
                                                                sx={{ height: 20, fontSize: '0.75rem' }}
                                                            />
                                                        )}
                                                    </Stack>
                                                    <Typography variant="body2" color="text.secondary">
                                                        {group.description}
                                                    </Typography>
                                                </Box>

                                                {/* Variant Chips */}
                                                <Stack direction="row" spacing="xs" sx={{ flexShrink: 0, alignSelf: 'center' }}>
                                                    {variants.map(({ card, chipLabel }) => {
                                                        const isDisabled = hasReachedCardLimit && !card.enabled;
                                                        return (
                                                            <MuiChip
                                                                key={card.id}
                                                                label={chipLabel}
                                                                icon={card.enabled ? <CheckIcon sx={{ fontSize: 14 }} /> : undefined}
                                                                size="small"
                                                                variant={card.enabled ? 'filled' : 'outlined'}
                                                                onClick={() => onToggleCard(card.id)}
                                                                disabled={isDisabled}
                                                                sx={card.enabled
                                                                    ? {
                                                                        bgcolor: (theme) => theme.palette.primary.light,
                                                                        color: (theme) => theme.palette.primary.contrastText,
                                                                        '& .MuiChip-icon': {
                                                                            color: (theme) => theme.palette.primary.contrastText,
                                                                        },
                                                                        '&:hover': {
                                                                            bgcolor: (theme) => theme.palette.primary.main,
                                                                        },
                                                                    }
                                                                    : {
                                                                        borderColor: (theme) => theme.palette.border.dark,
                                                                        color: (theme) => theme.palette.primary.main,
                                                                        '&:hover': {
                                                                            borderColor: (theme) => theme.palette.primary.light,
                                                                            bgcolor: 'action.hover',
                                                                        },
                                                                    }
                                                                }
                                                            />
                                                        );
                                                    })}
                                                </Stack>
                                            </Box>
                                            {group.groupName === 'Monthly Services' && kpiMetricOptions}
                                            </Fragment>
                                        );
                                    })}
                                </Stack>
                            </Collapse>
                        </Box>
                    );
                })}
            </Stack>
        </Box>
    );
};
